import { addDays } from "date-fns/addDays";
import { CalendarClock, Check } from "lucide-react";
import { useState } from "react";
import {
  useGetMany,
  useListContext,
  useNotify,
  useRefresh,
  useTranslate,
  useUpdate,
  useUpdateMany,
} from "ra-core";
import { Button } from "@/components/ui/button";

import type { Task } from "../types";
import { TaskBulkReassignButton } from "./TaskBulkReassignButton";

/** Same step as the row's "postpone to next week", applied to a selection. */
const POSTPONE_DAYS = 7;

/**
 * Bulk actions for the task list page.
 *
 * Selection comes from the page's `<List>`, so these operate on whatever the
 * rows put in it, whichever due-date bucket they were selected from.
 */
export const TasksBulkActions = () => (
  <>
    <TaskBulkCompleteButton />
    <TaskBulkPostponeButton />
    <TaskBulkReassignButton />
  </>
);

const TaskBulkCompleteButton = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const { selectedIds = [], onUnselectItems } = useListContext<Task>();
  const [updateMany, { isPending }] = useUpdateMany();

  const handleComplete = () => {
    updateMany(
      "tasks",
      { ids: selectedIds, data: { done_date: new Date().toISOString() } },
      {
        onSuccess: () => {
          notify("ra.notification.updated", {
            type: "info",
            messageArgs: { smart_count: selectedIds.length },
          });
          onUnselectItems();
          refresh();
        },
        onError: () => notify("ra.notification.http_error", { type: "error" }),
      },
    );
  };

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className="h-9"
      disabled={isPending}
      onClick={handleComplete}
    >
      <Check />
      {translate("resources.tasks.bulk.complete")}
    </Button>
  );
};

const TaskBulkPostponeButton = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const { selectedIds = [], onUnselectItems } = useListContext<Task>();
  const [update] = useUpdate<Task>(undefined, undefined, {
    returnPromise: true,
  });
  const [isPostponing, setIsPostponing] = useState(false);

  const { data: selectedTasks = [], isPending: isPendingTasks } =
    useGetMany<Task>(
      "tasks",
      { ids: selectedIds },
      { enabled: selectedIds.length > 0 },
    );

  // Each task moves relative to its own due date, so a 09:00 call stays a 09:00
  // call. One shared target date would silently retime the whole selection.
  // A task with no due date has nothing to move relative to, so it is scheduled
  // from today instead -- `new Date(null)` is the epoch, which would date it to
  // 1970 and leave it permanently overdue.
  const handlePostpone = async () => {
    setIsPostponing(true);
    try {
      await Promise.all(
        selectedTasks.map((task) =>
          update("tasks", {
            id: task.id,
            data: {
              due_date: addDays(
                task.due_date ? new Date(task.due_date) : new Date(),
                POSTPONE_DAYS,
              ).toISOString(),
            },
            previousData: task,
          }),
        ),
      );
      notify("ra.notification.updated", {
        type: "info",
        messageArgs: { smart_count: selectedTasks.length },
      });
      onUnselectItems();
      refresh();
    } catch {
      notify("ra.notification.http_error", { type: "error" });
    } finally {
      setIsPostponing(false);
    }
  };

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className="h-9"
      disabled={isPendingTasks || isPostponing}
      onClick={handlePostpone}
    >
      <CalendarClock />
      {translate("resources.tasks.bulk.postpone")}
    </Button>
  );
};
